/**
 * TabBar - Underlined tab strip used to switch between sections of a page.
 *
 * Atomic Design role: Molecule. A row of tab buttons plus the shared bottom
 * rule; owns only "which tab is active" presentation, never the panel content.
 *
 * Extracted because AdminPage and CMSPage each carried their own copy of the
 * same markup (five hand-written buttons between them) and had already started
 * to drift — AdminPage wrapped onto multiple rows, CMSPage scrolled sideways,
 * and both rendered a 39px-tall tap target, under the 44px touch minimum.
 *
 * @example
 * <TabBar
 *     tabs={[{ key: 'users', label: 'Users' }, { key: 'jobs', label: 'Jobs' }]}
 *     activeKey={activeTab}
 *     onChange={setActiveTab}
 * />
 */
import React from 'react';

export interface TabBarItem {
	/** Stable identifier reported back through onChange. */
	key: string;
	/** Visible label. */
	label: string;
}

export interface TabBarProps {
	/** Tabs to render, in display order. Filter before passing — the bar shows what it is given. */
	tabs: TabBarItem[];
	/** Key of the currently active tab. */
	activeKey: string;
	/** Called with the key of the tab the user picked. */
	onChange: (key: string) => void;
	/**
	 * Let tabs wrap onto additional rows instead of scrolling sideways.
	 * Use when the set is long enough that off-screen tabs would go unnoticed;
	 * the default single scrolling row keeps the underline continuous.
	 */
	wrap?: boolean;
	/** Optional extra classes merged onto the root. */
	className?: string;
}

export const TabBar: React.FC<TabBarProps> = ({ tabs, activeKey, onChange, wrap = false, className = '' }) => (
	<div
		className={[
			'tab-bar',
			'flex items-center gap-4px border-b border-brand-border mb-24px',
			/* Wrapping bars need no scroll affordance; scrolling ones must not
			   squeeze their tabs, hence flex-shrink-0 on each button below. */
			wrap ? 'flex-wrap' : 'overflow-x-auto whitespace-nowrap',
			className,
		].filter(Boolean).join(' ')}
		role="tablist"
		data-ref="molecule-tab-bar"
	>
		{tabs.map((tab) => {
			const isActive = activeKey === tab.key;
			return (
				<button
					key={tab.key}
					type="button"
					role="tab"
					aria-selected={isActive}
					onClick={() => onChange(tab.key)}
					className={[
						'tab-bar__tab',
						isActive ? 'tab-bar__tab--active' : '',
						/* min-h-44px on touch viewports; desktop keeps the denser
						   original height where the pointer is a mouse. */
						'flex-shrink-0 px-16px py-8px min-h-44px desktop:min-h-0 text-14px font-medium transition-colors border-b-2 -mb-px',
						isActive
							? 'border-brand-primary text-brand-primary'
							: 'border-transparent text-brand-text-subtle hover:text-brand-text hover:border-brand-border',
					].filter(Boolean).join(' ')}
				>
					{tab.label}
				</button>
			);
		})}
	</div>
);

export default TabBar;
